import { Avatar, Group, Menu, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { forwardRef } from "react";
import { BookOpen, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { colors } from "../theme/colors";

function getInitials(fullname: string | undefined, fallback: string): string {
	if (fullname?.trim()) {
		const parts = fullname.trim().split(/\s+/);
		if (parts.length >= 2) {
			return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
		}
		return fullname.slice(0, 2).toUpperCase();
	}
	return fallback.slice(0, 2).toUpperCase() || "U";
}

interface AvatarTriggerProps extends React.ComponentPropsWithoutRef<"button"> {
	initials: string;
}

const AvatarTrigger = forwardRef<HTMLButtonElement, AvatarTriggerProps>(
	({ initials, ...others }, ref) => (
		<UnstyledButton
			ref={ref}
			style={{
				padding: 0,
				borderRadius: "var(--mantine-radius-xl)",
			}}
			{...others}
		>
			<Avatar
				radius="xl"
				size="md"
				color="primary"
				className="border border-brand-400"
			>
				<span className="font-normal">{initials}</span>
			</Avatar>
		</UnstyledButton>
	),
);
AvatarTrigger.displayName = "AvatarTrigger";

export function Header() {
	const { user, logout } = useAuth();
	const displayName = user?.name?.trim() || user?.userId || "User";
	const initials = getInitials(user?.name, user?.userId ?? "U");

	return (
		<header
			className="h-14 flex items-center justify-between px-6 fixed top-0 left-0 right-0 z-50"
			style={{
				backgroundColor: colors.primary.background,
				color: colors.primary.text,
			}}
		>
			<Link
				to="/"
				className="flex items-center gap-3 no-underline"
				style={{ color: colors.primary.text }}
			>
				<div
					className="w-8 h-8 rounded flex items-center justify-center font-bold text-sm"
					style={{
						backgroundColor: colors.neutral.grey[700],
						color: colors.primary.text,
					}}
				>
					TLT
				</div>
				<h1 className="text-lg font-semibold">Employee Time Tracking System</h1>
			</Link>

			<div className="flex items-center gap-3">
				<Tooltip label="Time Tracking App Manual">
					<a
						href="https://porrag.sharepoint.com/sites/DE_BUAT/Manag/09_OPERATIONAL%20EXCELLENCE%20%26%20AI/01_Project%20Portfolio/Applications/MANUALS_FOR_ONLINE_VIEW/Anleitung%20TimeTracking%20App.pdf?csf=1&web=1&e=HOnBIE"
						target="_blank"
						rel="noopener noreferrer"
						className="flex items-center justify-center rounded-md p-2 no-underline transition-opacity hover:opacity-80"
						style={{ color: colors.primary.text }}
						aria-label="Time Tracking App Manual"
					>
						<BookOpen size={22} strokeWidth={1.75} />
					</a>
				</Tooltip>
				<Menu shadow="md" width={280} position="bottom-end" offset={8}>
					<Menu.Target>
						<AvatarTrigger initials={initials} />
					</Menu.Target>

					<Menu.Dropdown className="border border-border-default p-0 overflow-hidden">
						<div className="p-3">
							<Group gap="sm">
								<Avatar
									radius="xl"
									size="lg"
									color="primary"
									className="border border-brand-200"
								>
									<span className="font-normal">{initials}</span>
								</Avatar>
								<div>
									<Text size="sm" fw={500} className="text-text-primary">
										{displayName}
									</Text>
									{user?.email && (
										<Text size="xs" className="text-text-secondary">
											{user.email}
										</Text>
									)}
								</div>
							</Group>
						</div>
						<div className="bg-brand-100 px-3 py-2">
							<Menu.Item
								leftSection={<LogOut size={14} />}
								component="button"
								type="button"
								className="text-primary-600"
								onClick={() => void logout()}
							>
								Sign out
							</Menu.Item>
						</div>
					</Menu.Dropdown>
				</Menu>
			</div>
		</header>
	);
}
